import { useState, useCallback, useMemo } from "react";
import axios from "axios";

export default function TranscriptForm({ isDarkMode }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [showPreviewTimestamps, setShowPreviewTimestamps] = useState(true);

  // Detect mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const handleSubmit = async () => {
    if (!url) {
      alert("Please enter a YouTube URL");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post("/api/transcript/", {
        url: url,
        target_lang: null
      });

      if (res.data.success) {
        const videoId = res.data.video_id;
        const snippets = res.data.snippets || [];
        const transcript_text = snippets.map(s => `[${s.timestamp}] ${s.text}`).join('\n');

        let duration_seconds = 0;
        snippets.forEach(s => {
          const end = (s.start || 0) + (s.duration || 0);
          if (end > duration_seconds) duration_seconds = end;
        });

        const word_count = transcript_text.split(/\s+/).filter(Boolean).length;

        let title = '';
        let channel_name = '';
        try {
          const oe = await axios.get(`/api/youtube/oembed/`, {
            params: { video_id: videoId },
            timeout: 20000,
          });
          if (oe.data && !oe.data.error) {
            title = oe.data.title || '';
            channel_name = oe.data.author_name || '';
          } else {
            title = `Video ${videoId}`;
          }
        } catch (e) {
          console.warn('oEmbed failed', e.message);
          title = `Video ${videoId}`;
        }

        try {
          const token = localStorage.getItem('token');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};

          const createRes = await axios.post('/api/create_transcript/', {
            youtube_id: videoId,
            title: title || `Video ${videoId}`,
            channel_name,
            duration_seconds,
            word_count,
            language: res.data.language_code || 'en',
            transcript_text
          }, { headers });

          if (createRes.data && createRes.data.success) {
            window.location.href = `/t/${videoId}/`;
          }
        } catch (e) {
          console.error('Failed to create transcript record', e);
          alert('Error saving transcript. Please try again.');
        }
      } else {
        alert("Error: " + res.data.error);
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching transcript. Please try again.");
    }

    setLoading(false);
  };

  const toggleFaq = useCallback((index) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  }, []);

  const copyPreviewTranscript = useCallback(async () => {
    const content = previewTranscriptLines
      .map((line) => `${showPreviewTimestamps ? `${line.timestamp} ` : ""}${line.text}`)
      .join("\n");

    try {
      await navigator.clipboard.writeText(content);
      alert("Transcript copied to clipboard!");
    } catch (error) {
      console.error("Copy failed", error);
      alert("Unable to copy transcript.");
    }
  }, [showPreviewTimestamps]);

  const downloadPreviewTranscript = useCallback((format) => {
    const content = previewTranscriptLines
      .map((line) => `${showPreviewTimestamps ? `${line.timestamp} ` : ""}${line.text}`)
      .join("\n");

    const filename = `cliptranscript-preview.${format}`;
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/plain;charset=utf-8," + encodeURIComponent(content)
    );
    element.setAttribute("download", filename);

    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }, [showPreviewTimestamps]);

  const previewTranscriptLines = [
    { timestamp: "0:00", text: "Welcome back to the channel. Today we're going to dive deep into the science of building better habits." },
    { timestamp: "0:08", text: "I've spent the last six months researching behavioral psychology and I'm excited to share what I've learned." },
    { timestamp: "0:15", text: "Let's start with a fundamental question: why do we struggle to build new habits in the first place?" },
    { timestamp: "0:24", text: "The answer lies in how our brains are wired. We're designed to conserve energy and stick with familiar patterns." }
  ];

  const faqItems = [
    {
      question: "How do you generate the transcripts?",
      answer: "We use the official YouTube captions API to fetch transcripts. This ensures accuracy and reliability, as we're pulling the same captions that YouTube displays on videos."
    },
    {
      question: "Is ClipTranscript really free to use?",
      answer: "Yes! ClipTranscript is free to use for generating transcripts from YouTube videos."
    },
    {
      question: "Can I export the transcript?",
      answer: "Absolutely. You can copy the transcript to your clipboard or download it as a TXT or SRT file."
    },
    {
      question: "Do you support multiple languages?",
      answer: "We support any language that YouTube provides captions for. If the video has captions in multiple languages, you can select them on YouTube before generating the transcript."
    },
    {
      question: "Do you store transcripts?",
      answer: "We store transcripts briefly to power features like sharing and AI tools, but we do not use them for any purpose beyond running the service."
    }
  ];

  // ===== DESKTOP STYLES (ORIGINAL) =====
  const desktopContainerStyle = {
    padding: "30px 40px",
    textAlign: "center",
    minHeight: "calc(100vh - 180px)",
    backgroundColor: isDarkMode ? "#0d0d0d" : "#f8f9fa",
    transition: "background-color 0.3s"
  };

  const desktopContentStyle = {
    maxWidth: "700px",
    margin: "0 auto"
  };

  const desktopTitleStyle = {
    fontSize: "42px",
    marginBottom: "10px",
    fontWeight: "bold",
    color: isDarkMode ? "#ffffff" : "#000000"
  };

  const desktopSubtitleStyle = {
    fontSize: "18px",
    color: isDarkMode ? "#b0b0b0" : "#666",
    marginBottom: "20px"
  };

  const desktopInputContainerStyle = {
    display: "flex",
    gap: "10px",
    marginBottom: "15px"
  };

  const desktopInputStyle = {
    flex: 1,
    padding: "15px 20px",
    fontSize: "16px",
    border: `1px solid ${isDarkMode ? "#333" : "#ddd"}`,
    borderRadius: "5px",
    backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
    color: isDarkMode ? "#ffffff" : "#000000",
    boxShadow: isDarkMode ? "0 2px 4px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.1)",
    transition: "all 0.3s"
  };

  const desktopButtonStyle = {
    padding: "15px 40px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "5px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: loading ? "not-allowed" : "pointer",
    opacity: loading ? 0.7 : 1,
    transition: "all 0.3s"
  };

  const desktopFeatureContainerStyle = {
    display: "flex",
    justifyContent: "center",
    gap: "30px",
    color: isDarkMode ? "#b0b0b0" : "#666",
    fontSize: "14px",
    marginBottom: "20px",
    flexWrap: "wrap"
  };

  const desktopSectionStyle = {
    padding: "40px 0",
    borderTop: `1px solid ${isDarkMode ? "#222" : "#ececec"}`,
  };

  const desktopSectionTitleStyle = {
    fontSize: "34px",
    fontWeight: "bold",
    marginBottom: "10px",
    color: isDarkMode ? "#ffffff" : "#000000"
  };

  const desktopSectionSubtitleStyle = {
    fontSize: "16px",
    color: isDarkMode ? "#b0b0b0" : "#666",
    marginBottom: "20px",
    maxWidth: "800px"
  };

  const desktopCardGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "20px"
  };

  const desktopCardStyle = {
    backgroundColor: isDarkMode ? "#121212" : "#ffffff",
    border: `1px solid ${isDarkMode ? "#2a2a2a" : "#e5e5e5"}`,
    borderRadius: "14px",
    padding: "30px 25px",
    boxShadow: isDarkMode ? "0 10px 20px rgba(0,0,0,0.25)" : "0 10px 20px rgba(0,0,0,0.08)",
    minHeight: "190px"
  };

  const desktopCardTitleStyle = {
    fontSize: "18px",
    fontWeight: "600",
    marginTop: "15px",
    marginBottom: "12px",
    color: isDarkMode ? "#ffffff" : "#111"
  };

  const desktopCardTextStyle = {
    fontSize: "14px",
    color: isDarkMode ? "#b0b0b0" : "#555",
    lineHeight: "1.6"
  };

  const desktopPreviewContainerStyle = {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px"
  };

  const desktopPreviewCardStyle = {
    width: "100%",
    maxWidth: "760px",
    backgroundColor: isDarkMode ? "#0f0f0f" : "#ffffff",
    border: `1px solid ${isDarkMode ? "#2a2a2a" : "#e5e5e5"}`,
    borderRadius: "18px",
    overflow: "hidden",
    boxShadow: isDarkMode ? "0 20px 40px rgba(0,0,0,0.3)" : "0 20px 40px rgba(0,0,0,0.08)"
  };

  const desktopPreviewHeaderStyle = {
    padding: "18px 20px",
    borderBottom: `1px solid ${isDarkMode ? "#222" : "#ececec"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: isDarkMode ? "#111" : "#fafafa"
  };

  const desktopPreviewUrlStyle = {
    fontSize: "14px",
    color: isDarkMode ? "#b0b0b0" : "#444"
  };

  const desktopPreviewButtonsStyle = {
    display: "flex",
    gap: "10px"
  };

  const desktopSmallButtonStyle = {
    padding: "8px 14px",
    borderRadius: "999px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    transition: "all 0.2s"
  };

  const desktopPreviewLineStyle = {
    padding: "16px 20px",
    borderBottom: `1px solid ${isDarkMode ? "#222" : "#f1f1f1"}`,
    display: "flex",
    gap: "12px",
    alignItems: "flex-start"
  };

  const desktopTimestampStyle = {
    minWidth: "55px",
    color: isDarkMode ? "#4aa7ff" : "#0066cc",
    fontFamily: "monospace",
    fontSize: "13px",
    cursor: "pointer",
    fontWeight: "600"
  };

  const desktopPreviewTextStyle = {
    color: isDarkMode ? "#e5e5e5" : "#262626",
    fontSize: "14px",
    lineHeight: "1.6"
  };

  const desktopFaqContainerStyle = {
    maxWidth: "760px",
    margin: "0 auto",
    textAlign: "left"
  };

  const desktopFaqItemStyle = {
    borderTop: `1px solid ${isDarkMode ? "#222" : "#e8e8e8"}`,
    padding: "18px 0"
  };

  const desktopFaqButtonStyle = {
    width: "100%",
    background: "none",
    border: "none",
    padding: 0,
    fontSize: "16px",
    fontWeight: "600",
    color: isDarkMode ? "#ffffff" : "#111",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  };

  const desktopFaqAnswerStyle = {
    marginTop: "12px",
    fontSize: "14px",
    color: isDarkMode ? "#b0b0b0" : "#555",
    lineHeight: "1.7"
  };

  const desktopFaqIconStyle = {
    fontSize: "22px",
    color: isDarkMode ? "#888" : "#999"
  };

  // ===== MOBILE STYLES (OPTIMIZED) =====
  const mobileContainerStyle = {
    padding: "12px 16px",
    textAlign: "center",
    minHeight: "100vh",
    backgroundColor: isDarkMode ? "#0d0d0d" : "#f8f9fa",
    transition: "background-color 0.3s"
  };

  const mobileContentStyle = {
    maxWidth: "100%",
    margin: "0 auto"
  };

  const mobileTitleStyle = {
    fontSize: "24px",
    marginBottom: "6px",
    fontWeight: "bold",
    color: isDarkMode ? "#ffffff" : "#000000",
    lineHeight: 1.2
  };

  const mobileSubtitleStyle = {
    fontSize: "13px",
    color: isDarkMode ? "#b0b0b0" : "#666",
    marginBottom: "14px"
  };

  const mobileInputContainerStyle = {
    display: "flex",
    gap: "8px",
    marginBottom: "10px",
    flexDirection: "column"
  };

  const mobileInputStyle = {
    padding: "12px 14px",
    fontSize: "14px",
    border: `1px solid ${isDarkMode ? "#333" : "#ddd"}`,
    borderRadius: "6px",
    backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
    color: isDarkMode ? "#ffffff" : "#000000",
    boxShadow: isDarkMode ? "0 2px 4px rgba(0,0,0,0.3)" : "0 2px 4px rgba(0,0,0,0.1)",
    width: "100%"
  };

  const mobileButtonStyle = {
    padding: "12px 16px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "bold",
    cursor: loading ? "not-allowed" : "pointer",
    opacity: loading ? 0.7 : 1,
    transition: "all 0.3s",
    width: "100%"
  };

  const mobileFeatureContainerStyle = {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    color: isDarkMode ? "#b0b0b0" : "#666",
    fontSize: "11px",
    marginBottom: "12px",
    flexWrap: "wrap"
  };

  const mobileFeatureItemStyle = {
    padding: "4px 8px",
    backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
    borderRadius: "4px",
    border: `1px solid ${isDarkMode ? "#333" : "#ddd"}`
  };

  const mobileSectionStyle = {
    padding: "18px 0",
    borderTop: `1px solid ${isDarkMode ? "#222" : "#ececec"}`,
  };

  const mobileSectionTitleStyle = {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "6px",
    color: isDarkMode ? "#ffffff" : "#000000"
  };

  const mobileSectionSubtitleStyle = {
    fontSize: "12px",
    color: isDarkMode ? "#b0b0b0" : "#666",
    marginBottom: "12px"
  };

  const mobileCardGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
    gap: "10px"
  };

  const mobileCardStyle = {
    backgroundColor: isDarkMode ? "#121212" : "#ffffff",
    border: `1px solid ${isDarkMode ? "#2a2a2a" : "#e5e5e5"}`,
    borderRadius: "10px",
    padding: "12px 10px",
    boxShadow: isDarkMode ? "0 4px 12px rgba(0,0,0,0.2)" : "0 4px 12px rgba(0,0,0,0.06)",
    minHeight: "110px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  };

  const mobileCardTitleStyle = {
    fontSize: "12px",
    fontWeight: "600",
    marginTop: "8px",
    marginBottom: "4px",
    color: isDarkMode ? "#ffffff" : "#111"
  };

  const mobileCardTextStyle = {
    fontSize: "11px",
    color: isDarkMode ? "#b0b0b0" : "#555",
    lineHeight: "1.4"
  };

  const mobilePreviewContainerStyle = {
    display: "flex",
    justifyContent: "center",
    marginTop: "12px"
  };

  const mobilePreviewCardStyle = {
    width: "100%",
    backgroundColor: isDarkMode ? "#0f0f0f" : "#ffffff",
    border: `1px solid ${isDarkMode ? "#2a2a2a" : "#e5e5e5"}`,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: isDarkMode ? "0 8px 16px rgba(0,0,0,0.2)" : "0 8px 16px rgba(0,0,0,0.06)"
  };

  const mobilePreviewHeaderStyle = {
    padding: "10px 12px",
    borderBottom: `1px solid ${isDarkMode ? "#222" : "#ececec"}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: isDarkMode ? "#111" : "#fafafa",
    gap: "8px",
    flexWrap: "wrap"
  };

  const mobilePreviewUrlStyle = {
    fontSize: "11px",
    color: isDarkMode ? "#b0b0b0" : "#444",
    flex: 1
  };

  const mobilePreviewButtonsStyle = {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap"
  };

  const mobileSmallButtonStyle = {
    padding: "6px 10px",
    borderRadius: "999px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "600",
    transition: "all 0.2s",
    whiteSpace: "nowrap"
  };

  const mobilePreviewLineStyle = {
    padding: "10px 12px",
    borderBottom: `1px solid ${isDarkMode ? "#222" : "#f1f1f1"}`,
    display: "flex",
    gap: "8px",
    alignItems: "flex-start"
  };

  const mobileTimestampStyle = {
    minWidth: "40px",
    color: isDarkMode ? "#4aa7ff" : "#0066cc",
    fontFamily: "monospace",
    fontSize: "11px",
    cursor: "pointer",
    fontWeight: "600",
    flexShrink: 0
  };

  const mobilePreviewTextStyle = {
    color: isDarkMode ? "#e5e5e5" : "#262626",
    fontSize: "12px",
    lineHeight: "1.5"
  };

  const mobileFaqContainerStyle = {
    maxWidth: "100%",
    margin: "0 auto",
    textAlign: "left"
  };

  const mobileFaqItemStyle = {
    borderTop: `1px solid ${isDarkMode ? "#222" : "#e8e8e8"}`,
    padding: "12px 0"
  };

  const mobileFaqButtonStyle = {
    width: "100%",
    background: "none",
    border: "none",
    padding: 0,
    fontSize: "13px",
    fontWeight: "600",
    color: isDarkMode ? "#ffffff" : "#111",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  };

  const mobileFaqAnswerStyle = {
    marginTop: "8px",
    fontSize: "12px",
    color: isDarkMode ? "#b0b0b0" : "#555",
    lineHeight: "1.6"
  };

  const mobileFaqIconStyle = {
    fontSize: "16px",
    color: isDarkMode ? "#888" : "#999"
  };

  // ===== RENDER MOBILE =====
  if (isMobile) {
    return (
      <div style={mobileContainerStyle}>
        <div style={mobileContentStyle}>
          <h1 style={mobileTitleStyle}>Turn YouTube into text.</h1>
          <p style={mobileSubtitleStyle}>Fast, accurate. No ads. No sign-up.</p>

          <div style={mobileInputContainerStyle}>
            <input
              style={mobileInputStyle}
              type="text"
              placeholder="Paste YouTube link..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
            />
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={mobileButtonStyle}
            >
              {loading ? "Loading..." : "Get transcript →"}
            </button>
          </div>

          <div style={mobileFeatureContainerStyle}>
            <div style={mobileFeatureItemStyle}>📌 YouTube captions</div>
            <div style={mobileFeatureItemStyle}>🔓 No login</div>
            <div style={mobileFeatureItemStyle}>⬇️ Export</div>
          </div>

          <div style={{ color: isDarkMode ? "#b0b0b0" : "#666", marginBottom: "16px", fontSize: "12px" }}>
            ⭐⭐⭐⭐⭐ 50,000+ users daily
          </div>

          {/* How it works */}
          <section style={mobileSectionStyle} id="how-it-works">
            <h2 style={mobileSectionTitleStyle}>How it works</h2>
            <p style={mobileSectionSubtitleStyle}>3 simple steps</p>

            <div style={mobileCardGridStyle}>
              <div style={mobileCardStyle}>
                <div style={{ fontSize: "20px" }}>🔗</div>
                <div style={mobileCardTitleStyle}>Paste URL</div>
                <div style={mobileCardTextStyle}>Enter YouTube link</div>
              </div>

              <div style={mobileCardStyle}>
                <div style={{ fontSize: "20px" }}>📝</div>
                <div style={mobileCardTitleStyle}>Get text</div>
                <div style={mobileCardTextStyle}>In seconds</div>
              </div>

              <div style={mobileCardStyle}>
                <div style={{ fontSize: "20px" }}>⬇️</div>
                <div style={mobileCardTitleStyle}>Download</div>
                <div style={mobileCardTextStyle}>TXT or SRT</div>
              </div>
            </div>
          </section>

          {/* Preview */}
          <section style={mobileSectionStyle}>
            <h2 style={mobileSectionTitleStyle}>See it in action</h2>

            <div style={mobilePreviewContainerStyle}>
              <div style={mobilePreviewCardStyle}>
                <div style={mobilePreviewHeaderStyle}>
                  <div style={mobilePreviewUrlStyle}>▶️ How to build better habits</div>
                  <div style={mobilePreviewButtonsStyle}>
                    <button
                      style={{
                        ...mobileSmallButtonStyle,
                        backgroundColor: isDarkMode ? "#1b1b1b" : "#ffffff",
                        borderColor: isDarkMode ? "#333" : "#d1d1d1"
                      }}
                      onClick={copyPreviewTranscript}
                    >
                      📋
                    </button>
                    <button
                      style={{
                        ...mobileSmallButtonStyle,
                        backgroundColor: isDarkMode ? "#1b1b1b" : "#ffffff",
                        borderColor: isDarkMode ? "#333" : "#d1d1d1"
                      }}
                      onClick={() => downloadPreviewTranscript("txt")}
                    >
                      📄
                    </button>
                    <button
                      style={{
                        ...mobileSmallButtonStyle,
                        backgroundColor: isDarkMode ? "#1b1b1b" : "#ffffff",
                        borderColor: isDarkMode ? "#333" : "#d1d1d1"
                      }}
                      onClick={() => downloadPreviewTranscript("srt")}
                    >
                      🎬
                    </button>
                    <button
                      style={{
                        ...mobileSmallButtonStyle,
                        backgroundColor: showPreviewTimestamps
                          ? isDarkMode ? "#007bff" : "#e8f0ff"
                          : isDarkMode ? "#1b1b1b" : "#ffffff",
                        borderColor: isDarkMode ? "#333" : "#d1d1d1",
                        color: showPreviewTimestamps ? (isDarkMode ? "#fff" : "#007bff") : undefined
                      }}
                      onClick={() => setShowPreviewTimestamps((prev) => !prev)}
                    >
                      ⏱️
                    </button>
                  </div>
                </div>

                <div>
                  {previewTranscriptLines.map((line, index) => (
                    <div key={index} style={mobilePreviewLineStyle}>
                      {showPreviewTimestamps && (
                        <span style={mobileTimestampStyle}>
                          {line.timestamp}
                        </span>
                      )}
                      <span style={mobilePreviewTextStyle}>{line.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* AI Tools */}
          <section style={mobileSectionStyle} id="ai-tools">
            <h2 style={mobileSectionTitleStyle}>AI Tools</h2>
            <p style={mobileSectionSubtitleStyle}>Boost your content</p>

            <div style={mobileCardGridStyle}>
              <div style={mobileCardStyle}>
                <div style={{ fontSize: "20px" }}>🤖</div>
                <div style={mobileCardTitleStyle}>Summaries</div>
                <div style={mobileCardTextStyle}>Key points</div>
              </div>

              <div style={mobileCardStyle}>
                <div style={{ fontSize: "20px" }}>📑</div>
                <div style={mobileCardTitleStyle}>Chapters</div>
                <div style={mobileCardTextStyle}>Auto-gen</div>
              </div>

              <div style={mobileCardStyle}>
                <div style={{ fontSize: "20px" }}>🌐</div>
                <div style={mobileCardTitleStyle}>Translate</div>
                <div style={mobileCardTextStyle}>Multi-lang</div>
              </div>
            </div>

            <div style={{ textAlign: "center", marginTop: "12px" }}>
              <button
                onClick={() => alert("Try 3 AI tools for free")}
                style={{
                  ...mobileButtonStyle,
                  padding: "10px 16px",
                  fontSize: "13px"
                }}
              >
                Try 3 AI tools free
              </button>
            </div>
          </section>

          {/* FAQ */}
          <section style={mobileSectionStyle} id="faq">
            <h2 style={mobileSectionTitleStyle}>FAQ</h2>

            <div style={mobileFaqContainerStyle}>
              {faqItems.map((item, index) => (
                <div key={index} style={mobileFaqItemStyle}>
                  <button
                    style={mobileFaqButtonStyle}
                    onClick={() => toggleFaq(index)}
                    aria-expanded={openFaqIndex === index}
                  >
                    <span style={{ textAlign: "left" }}>{item.question}</span>
                    <span style={mobileFaqIconStyle}>{openFaqIndex === index ? "−" : "+"}</span>
                  </button>
                  {openFaqIndex === index && (
                    <div style={mobileFaqAnswerStyle}>{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    );
  }

  // ===== RENDER DESKTOP (ORIGINAL) =====
  return (
    <div style={desktopContainerStyle}>
      <div style={desktopContentStyle}>
        <h1 style={desktopTitleStyle}>
          Turn any YouTube video into text.
        </h1>

        <p style={desktopSubtitleStyle}>
          Fast, accurate. No ads. No sign-up required.
        </p>

        <div style={desktopInputContainerStyle}>
          <input
            style={desktopInputStyle}
            type="text"
            placeholder="Paste a YouTube link..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
          />

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={desktopButtonStyle}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = "#0056b3")}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = "#007bff")}
          >
            {loading ? "Loading..." : (
              <>
                Get transcript
                <span
                  style={{
                    fontSize: "22px",
                    fontWeight: "900",
                    marginLeft: "6px",
                    lineHeight: 0,
                    display: "inline-block",
                    transform: "translateY(2px)"
                  }}
                >
                  →
                </span>
              </>
            )}
          </button>
        </div>

        <div style={desktopFeatureContainerStyle} id="features">
          <div>Official YouTube captions</div>
          <div>No login required</div>
          <div>Export TXT/SRT</div>
        </div>

        <div style={{ color: isDarkMode ? "#b0b0b0" : "#666", marginBottom: "40px" }}>
          ⭐⭐⭐⭐⭐ Trusted by 50,000+ users daily
        </div>

        <section style={desktopSectionStyle} id="how-it-works">
          <h2 style={desktopSectionTitleStyle}>How it works</h2>
          <p style={desktopSectionSubtitleStyle}>Three simple steps to get your transcript</p>

          <div style={desktopCardGridStyle}>
            <div style={desktopCardStyle}>
              <div style={{ fontSize: "22px" }}>🔗</div>
              <div style={desktopCardTitleStyle}>Paste the URL</div>
              <div style={desktopCardTextStyle}>Enter any YouTube link to begin.</div>
            </div>

            <div style={desktopCardStyle}>
              <div style={{ fontSize: "22px" }}>📝</div>
              <div style={desktopCardTitleStyle}>Get the transcript</div>
              <div style={desktopCardTextStyle}>We fetch the official captions in seconds.</div>
            </div>

            <div style={desktopCardStyle}>
              <div style={{ fontSize: "22px" }}>⬇️</div>
              <div style={desktopCardTitleStyle}>Copy or download</div>
              <div style={desktopCardTextStyle}>Copy text or export as TXT or SRT.</div>
            </div>
          </div>
        </section>

        <section style={desktopSectionStyle}>
          <h2 style={desktopSectionTitleStyle}>See how it works in action</h2>
          <p style={desktopSectionSubtitleStyle}>From YouTube link to text in seconds</p>

          <div style={desktopPreviewContainerStyle}>
            <div style={desktopPreviewCardStyle}>
              <div style={desktopPreviewHeaderStyle}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "16px" }}>▶️</span>
                  <div style={desktopPreviewUrlStyle}>How to build better habits</div>
                </div>
                <div style={desktopPreviewButtonsStyle}>
                  <button
                    style={{
                      ...desktopSmallButtonStyle,
                      backgroundColor: isDarkMode ? "#1b1b1b" : "#ffffff",
                      borderColor: isDarkMode ? "#333" : "#d1d1d1"
                    }}
                    onClick={copyPreviewTranscript}
                    title="Copy transcript"
                  >
                    📋 Copy
                  </button>
                  <button
                    style={{
                      ...desktopSmallButtonStyle,
                      backgroundColor: isDarkMode ? "#1b1b1b" : "#ffffff",
                      borderColor: isDarkMode ? "#333" : "#d1d1d1"
                    }}
                    onClick={() => downloadPreviewTranscript("txt")}
                    title="Download as TXT"
                  >
                    📄 TXT
                  </button>
                  <button
                    style={{
                      ...desktopSmallButtonStyle,
                      backgroundColor: isDarkMode ? "#1b1b1b" : "#ffffff",
                      borderColor: isDarkMode ? "#333" : "#d1d1d1"
                    }}
                    onClick={() => downloadPreviewTranscript("srt")}
                    title="Download as SRT"
                  >
                    🎬 SRT
                  </button>
                  <button
                    style={{
                      ...desktopSmallButtonStyle,
                      backgroundColor: showPreviewTimestamps
                        ? isDarkMode ? "#007bff" : "#e8f0ff"
                        : isDarkMode ? "#1b1b1b" : "#ffffff",
                      borderColor: isDarkMode ? "#333" : "#d1d1d1",
                      color: showPreviewTimestamps ? (isDarkMode ? "#fff" : "#007bff") : undefined
                    }}
                    onClick={() => setShowPreviewTimestamps((prev) => !prev)}
                    title="Toggle timestamps"
                  >
                    {showPreviewTimestamps ? "⏱️ Hide" : "⏱️ Show"}
                  </button>
                </div>
              </div>

              <div>
                {previewTranscriptLines.map((line, index) => (
                  <div key={index} style={desktopPreviewLineStyle}>
                    {showPreviewTimestamps && (
                      <span style={desktopTimestampStyle}>
                        <span style={{ color: isDarkMode ? "#666" : "#999" }}>▌</span> {line.timestamp}
                      </span>
                    )}
                    <span style={desktopPreviewTextStyle}>{line.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section style={desktopSectionStyle} id="ai-tools">
          <h2 style={desktopSectionTitleStyle}>Boost your content with AI</h2>
          <p style={desktopSectionSubtitleStyle}>Create more in less time with our powerful AI tools.</p>

          <div style={desktopCardGridStyle}>
            <div style={desktopCardStyle}>
              <div style={{ fontSize: "22px" }}>🤖</div>
              <div style={desktopCardTitleStyle}>AI Summaries</div>
              <div style={desktopCardTextStyle}>Get key points fast.</div>
            </div>

            <div style={desktopCardStyle}>
              <div style={{ fontSize: "22px" }}>📑</div>
              <div style={desktopCardTitleStyle}>Chapters</div>
              <div style={desktopCardTextStyle}>Auto-generate Chapters.</div>
            </div>

            <div style={desktopCardStyle}>
              <div style={{ fontSize: "22px" }}>🌐</div>
              <div style={desktopCardTitleStyle}>Translate Text</div>
              <div style={desktopCardTextStyle}>Translate to multiple languages.</div>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: "35px" }}>
            <button
              onClick={() => alert("Try 3 AI tools for free")}
              style={{
                ...desktopButtonStyle,
                padding: "14px 32px",
                backgroundColor: "#007bff"
              }}
            >
              Try 3 AI tools for free
            </button>
          </div>
        </section>

        <section style={desktopSectionStyle} id="faq">
          <h2 style={desktopSectionTitleStyle}>Frequently Asked Questions</h2>
          <p style={desktopSectionSubtitleStyle}>Everything you need to know about ClipTranscript</p>

          <div style={desktopFaqContainerStyle}>
            {faqItems.map((item, index) => (
              <div key={index} style={desktopFaqItemStyle}>
                <button
                  style={desktopFaqButtonStyle}
                  onClick={() => toggleFaq(index)}
                  aria-expanded={openFaqIndex === index}
                >
                  <span>{item.question}</span>
                  <span style={desktopFaqIconStyle}>{openFaqIndex === index ? "−" : "+"}</span>
                </button>
                {openFaqIndex === index && (
                  <div style={desktopFaqAnswerStyle}>{item.answer}</div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
